import { Injectable } from '@angular/core';
import { Action, Selector, State, StateToken, type StateContext } from '@ngxs/store';
import * as FlatModels from '@medsurf/flat-models';
import * as FlatActions from '@medsurf/flat-actions';
import { Undoable } from '@medsurf/flat-decorators';
import { HistoryEntityBaseState } from '../base/history-entity.base.state';

export const KEYMAP_STYLE_ENTITY_TOKEN = new StateToken<KeymapStyleEntityStateModel>(FlatModels.EntityControlModels.EntityQueryType.enum.KEYMAP_STYLE_ENTITY);

export type KeymapStyleEntityStateModel = FlatModels.HistoryControlEntityStatesModels.HistoryControlEntityStateModel<FlatModels.KeymapStyleEntityModels.KeymapStyle>;

@State<KeymapStyleEntityStateModel>({
  name: KEYMAP_STYLE_ENTITY_TOKEN,
  defaults: {
    snapshots: {},
    entities: {},
  }
})
@Injectable()
export class KeymapStyleEntityState extends HistoryEntityBaseState<FlatModels.KeymapStyleEntityModels.KeymapStyle, KeymapStyleEntityStateModel> {
  //<editor-fold desc="Selectors">

  @Selector([KeymapStyleEntityState])
  static override typedEntities$(state: KeymapStyleEntityStateModel): { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle } {
    return HistoryEntityBaseState.typedEntities$(state);
  }

  @Selector([KeymapStyleEntityState.typedEntities$])
  static override entities$(typedEntities: { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle }): FlatModels.KeymapStyleEntityModels.KeymapStyle[] {
    return HistoryEntityBaseState.entities$(typedEntities);
  }

  @Selector([KeymapStyleEntityState.typedEntities$])
  static override entity$(typedEntities: { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle }) {
    return HistoryEntityBaseState.entity$(typedEntities);
  }

  @Selector([KeymapStyleEntityState.typedEntities$])
  static override entitiesByIds$(typedEntities: { [id: string]: FlatModels.KeymapStyleEntityModels.KeymapStyle }) {
    return HistoryEntityBaseState.entitiesByIds$(typedEntities);
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  //<editor-fold desc="Insertion">

  @Action(FlatActions.KeymapStyleEntityActions.GetEntitySuccess)
  override getEntitySuccess(state: StateContext<KeymapStyleEntityStateModel>,
                            action: FlatActions.KeymapStyleEntityActions.GetEntitySuccess) {
    return super.getEntitySuccess(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.GetEntitiesSuccess)
  override getEntitiesSuccess(state: StateContext<KeymapStyleEntityStateModel>,
                              action: FlatActions.KeymapStyleEntityActions.GetEntitiesSuccess) {
    return super.getEntitiesSuccess(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.ModifyEntitySuccess)
  override modifyEntitySuccess(state: StateContext<KeymapStyleEntityStateModel>,
                               action: FlatActions.KeymapStyleEntityActions.ModifyEntitySuccess) {
    return super.modifyEntitySuccess(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.ModifyEntitiesSuccess)
  override modifyEntitiesSuccess(state: StateContext<KeymapStyleEntityStateModel>,
                                 action: FlatActions.KeymapStyleEntityActions.ModifyEntitiesSuccess) {
    return super.modifyEntitiesSuccess(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Edit">

  @Action(FlatActions.KeymapStyleEntityActions.AddEntity)
  @Undoable(
    FlatActions.KeymapStyleEntityActions.AddEntity,
    KEYMAP_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.ADD
  )
  override addEntity(state: StateContext<KeymapStyleEntityStateModel>,
                     action: FlatActions.KeymapStyleEntityActions.AddEntity) {
    return super.addEntity(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.UpdateEntity)
  @Undoable(
    FlatActions.KeymapStyleEntityActions.UpdateEntity,
    KEYMAP_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.UPDATE
  )
  override updateEntity(state: StateContext<KeymapStyleEntityStateModel>,
                        action: FlatActions.KeymapStyleEntityActions.UpdateEntity) {
    return super.updateEntity(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.RemoveEntity)
  @Undoable(
    FlatActions.KeymapStyleEntityActions.RemoveEntity,
    KEYMAP_STYLE_ENTITY_TOKEN,
    FlatModels.EntityControlModels.HistorySnapshotItemModifierType.enum.REMOVE
  )
  override removeEntity(state: StateContext<KeymapStyleEntityStateModel>,
                        action: FlatActions.KeymapStyleEntityActions.RemoveEntity) {
    return super.removeEntity(state, action);
  }

  //</editor-fold>

  //<editor-fold desc="Snapshot">

  @Action(FlatActions.KeymapStyleEntityActions.UndoSnapshot)
  override undoSnapshot(state: StateContext<KeymapStyleEntityStateModel>,
                        action: FlatActions.KeymapStyleEntityActions.UndoSnapshot) {
    return super.undoSnapshot(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.RedoSnapshot)
  override redoSnapshot(state: StateContext<KeymapStyleEntityStateModel>,
                        action: FlatActions.KeymapStyleEntityActions.RedoSnapshot) {
    return super.redoSnapshot(state, action);
  }

  @Action(FlatActions.KeymapStyleEntityActions.CleanUpSnapshots)
  override cleanUpSnapshots(state: StateContext<KeymapStyleEntityStateModel>,
                            action: FlatActions.KeymapStyleEntityActions.CleanUpSnapshots) {
    return super.cleanUpSnapshots(state, action);
  }

  //</editor-fold>

  //</editor-fold>
}
